import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Loader2, Eye } from "lucide-react";

const SCOPE_LABEL = {
  people: "People (Candidates, Contacts, Submissions, Interviews, Offers, Placements, Invoices)",
  employees: "Employees / HRMS (Employee master, Attendance, Leave, Payroll, Payslips, Timesheets)",
  sales: "Sales & Clients (Leads, Clients, R.A. handoffs)",
  ra_workspace: "R.A. Workspace (Leads, Worksheets, Sync history, Catalog & Dispatch)",
  jobs: "Jobs (Requisitions & Contractors)",
  users: "Internal Users (Super Admin preserved)",
  everything: "Everything — full wipe (Super Admin & Audit logs preserved)",
};

export default function DataCleanup() {
  const [scopes, setScopes] = useState([]);
  const [scope, setScope] = useState("people");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api
      .get("/admin/data-cleanup/scopes")
      .then(({ data }) => setScopes(data.scopes || []))
      .catch(() => {});
  }, []);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await api.get("/admin/data-cleanup/preview", {
        params: { scope },
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const runWipe = async () => {
    if (typed !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post("/admin/data-cleanup", {
        scope,
        confirm: "DELETE",
      });
      setLastResult(data);
      toast.success(`Removed ${data.total_removed} records across ${scope}`);
      setConfirmOpen(false);
      setTyped("");
      setPreview(null);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setRunning(false);
    }
  };

  const currentScope = scopes.find((s) => s.key === scope);

  return (
    <Card className="p-5 border-red-300 space-y-4" data-testid="data-cleanup-card">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
        <div>
          <div className="font-display font-semibold">Data Cleanup — irreversible</div>
          <p className="text-sm text-muted-foreground">
            Permanently delete Nexreq records. Super Admin account and audit logs are
            always preserved. Every wipe is written to the audit log.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 items-end">
        <div className="md:col-span-2 space-y-1.5">
          <Label className="text-xs">Scope</Label>
          <Select value={scope} onValueChange={(v) => { setScope(v); setPreview(null); }}>
            <SelectTrigger data-testid="cleanup-scope-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SCOPE_LABEL).map(([k, label]) => (
                <SelectItem key={k} value={k}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentScope?.notes && (
            <p className="text-[11px] text-muted-foreground pt-1">{currentScope.notes}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadPreview}
            disabled={loading}
            data-testid="cleanup-preview-btn"
            className="gap-1.5"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            Preview
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
            disabled={!preview || preview.total === 0}
            onClick={() => setConfirmOpen(true)}
            data-testid="cleanup-open-confirm-btn"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border border-border p-3" data-testid="cleanup-preview">
          <div className="text-sm">
            <span className="font-medium">{preview.total}</span> record
            {preview.total === 1 ? "" : "s"} will be removed from{" "}
            <span className="font-medium">{preview.scope}</span>.
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(preview.per_collection || {}).map(([coll, n]) => (
              <div
                key={coll}
                className="text-xs px-2 py-1.5 rounded border border-border flex justify-between"
              >
                <span className="text-muted-foreground">{coll}</span>
                <span className="font-medium">{n}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {lastResult && (
        <div
          className="rounded-lg border border-[#2f9e5f]/30 bg-[#2f9e5f]/5 p-3 text-sm"
          data-testid="cleanup-result"
        >
          Removed <b>{lastResult.total_removed}</b> records from{" "}
          <b>{lastResult.scope}</b>.
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Type DELETE to confirm</DialogTitle>
            <DialogDescription>
              This permanently removes {preview?.total ?? "?"} records from{" "}
              <b>{scope}</b>. Super Admin and audit logs will be preserved.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="cleanup-confirm-input"
            autoFocus
            placeholder="DELETE"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false);
                setTyped("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={runWipe}
              disabled={running || typed !== "DELETE"}
              data-testid="cleanup-run-btn"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete forever"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
